import { useId } from "react";
import { STATUS_HINT, STATUS_META } from "../data/status";
import type { RequestStatus } from "../data/types";

/**
 * Coloured dot plus label for a request status; colour never carries it alone.
 * Statuses in STATUS_HINT get a hover tooltip, worded for `audience`.
 * `focusable` also opens it on keyboard focus and describes the label with it —
 * only outside a link, where a focus stop would be invalid (hover-only there).
 */
export function StatusLabel({ status, audience = "staff", focusable = false }: { status: RequestStatus; audience?: "staff" | "customer"; focusable?: boolean }) {
  const meta = STATUS_META[status];
  const tipId = useId();
  const hint = STATUS_HINT[status];
  const text = hint ? (audience === "customer" ? (hint.customer ?? hint.staff) : hint.staff) : null;

  // Opens leftward from the label's right edge, so it stays inside the clipped
  // table cards: the status is never a table's first column.
  const content = (
    <>
      <i aria-hidden="true" className={`h-2 w-2 flex-none rounded-full ${meta.dot}`} />
      {meta.label}
    </>
  );
  if (!text) return <span className="inline-flex items-center gap-2 whitespace-nowrap text-gray-800">{content}</span>;

  return (
    <span
      tabIndex={focusable ? 0 : undefined}
      aria-describedby={focusable ? tipId : undefined}
      className="group relative z-10 inline-flex w-max cursor-help items-center gap-2 whitespace-nowrap rounded-sm text-gray-800 underline decoration-dotted decoration-gray-400 underline-offset-4 focus-visible:focus-ring"
    >
      {content}
      <span
        id={tipId}
        role="tooltip"
        className="pointer-events-none invisible absolute bottom-full right-0 z-30 mb-1.5 w-max max-w-[15rem] whitespace-normal rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-normal leading-4 text-white no-underline opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100 group-focus-visible:visible group-focus-visible:opacity-100"
      >
        {text}
      </span>
    </span>
  );
}
